import { getCookies } from '@/libs/cookies'
import fetcher, { fetchWithToken } from '@/services/fetcher'
import { buildQuery } from '@/utils/helpers'
import { IAffiliateLevel } from '../types'

const QUERY_SET_REF = `
mutation setReferralCode($referral_code: String!) {  
  setReferralCode(referral_code: $referral_code)  
}`
const GET_LIST_AFFILIATE_LEVEL = `
query getListAffiliateLevel {
  getListAffiliateLevel {
    bonus
    cashback
    description
    discount
    image
    level
    parent_bonus
    title
    total_amount
  }
}
`

const UPDATE_AFFILIATE_CLICK = `
mutation updateAffiliateClick($referral_code: String!, $camp: String, $additional_data: String) {
  updateAffiliateClick(
    referral_code: $referral_code
    camp: $camp
    additional_data: $additional_data
  )
}`
/**
 * Set referral code API - Client-side version
 * Uses direct fetch with client-side cookies to avoid Server Actions hanging issue
 */
export const setReferralCodeAPI = async (referral_code: string) => {
  console.log('[setReferralCodeAPI] Starting API call with:', referral_code)

  try {
    // Get token from client-side cookies (no Server Actions)
    const token = (await getCookies('token')) || ''
    console.log('[setReferralCodeAPI] Token found:', token ? 'Yes' : 'No')

    if (!token) {
      console.error('[setReferralCodeAPI] No authentication token')
      return {
        status: 'error',
        message: 'Vui lòng đăng nhập để tiếp tục',
        data: null,
      }
    }

    const apiUrl = process.env.NEXT_PUBLIC_API_URL

    if (!apiUrl) {
      throw new Error('API URL not configured')
    }

    // Get Facebook pixels from cookies
    const fbp = (await getCookies('_fbp')) || ''
    const fbc = (await getCookies('_fbc')) || ''
    const locale = (await getCookies('locale')) || 'vi'

    const requestBody = {
      query: QUERY_SET_REF,
      variables: { referral_code },
    }

    console.log('[setReferralCodeAPI] Request body:', requestBody)

    const startTime = Date.now()

    // Direct fetch without Server Actions
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        _fbp: fbp,
        _fbc: fbc,
        Store: locale,
      },
      body: JSON.stringify(requestBody),
    })

    const duration = Date.now() - startTime
    console.log(`[setReferralCodeAPI] Response received in ${duration}ms`)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('[setReferralCodeAPI] HTTP Error:', response.status, errorText)
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const responseData = await response.json()
    console.log('[setReferralCodeAPI] Response data:', responseData)

    const data = responseData?.data?.setReferralCode
    console.log('[setReferralCodeAPI] Extracted data:', data)

    if (responseData?.errors) {
      console.error('[setReferralCodeAPI] GraphQL errors:', responseData.errors)
      return {
        status: 'error',
        message: responseData.errors[0]?.message || 'GraphQL error',
        data: null,
      }
    }

    console.log('[setReferralCodeAPI] ✅ Success!')
    return { status: 'success', data, message: '' }
  } catch (error) {
    console.error('[setReferralCodeAPI] ❌ Exception caught:', error)
    console.error('[setReferralCodeAPI] Error details:', {
      name: (error as Error)?.name,
      message: (error as Error)?.message,
      stack: (error as Error)?.stack,
    })
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'Lỗi kết nối API',
      data: null,
    }
  }
}

export const getListAffLevel = async () => {
  try {
    const res = await fetcher
      .get(buildQuery(GET_LIST_AFFILIATE_LEVEL), {
        next: { tags: ['list-affiliate-level'], revalidate: 180 },
        cache: 'force-cache',
      })
      .then((res) => res?.payload)

    const data = res?.data?.getListAffiliateLevel as IAffiliateLevel[]

    if (res?.errors) {
      return { status: 'error', message: res?.errors[0]?.message, data: null }
    }

    return { status: 'success', data, message: '' }
  } catch (error) {
    console.error('Error fetching list affiliate level:', error)
    return { status: 'error', message: 'Có lỗi xảy ra khi tải danh sách cấp độ liên kết', data: null }
  }
}

/**
 * Gọi API để đếm lượt click cho mã giới thiệu
 * @param referralCode - Mã giới thiệu từ URL param (ví dụ: HYDRA-019)
 * @returns Promise với kết quả hoặc undefined nếu có lỗi
 */
export const updateAffiliateClick = async (referralCode: string, camp: string, additionalData: string) => {
  try {
    const response = await fetchWithToken({
      query: UPDATE_AFFILIATE_CLICK,
      variables: {
        referral_code: referralCode,
        camp,
        additional_data: additionalData,
      },
    }).then((res) => res?.payload)

    if (response?.errors) {
      console.error('Error updating affiliate click:', response?.errors[0]?.message)
      return undefined
    }

    const data = response?.data?.updateAffiliateClick
    return data
  } catch (error) {
    // Silent fail - không throw error
    console.error('Error calling updateAffiliateClick:', error)
    return undefined
  }
}
